if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.form.querySelector('[name="return_to"]')?.remove();
        this.variantPicker = this.getVariantPicker();

        if (this.variantPicker) {
          this.syncSelectedVariantId();
          this.variantPicker.addEventListener('change', () => {
            this.syncSelectedVariantId();
            this.updateBrowserVariantUrl();
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.syncSelectedVariantId();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              try {
                this.cart.renderContents(response);
              } catch (e) {
                console.error(e);
                window.location = window.routes.cart_url;
              }
            }
          })
          .catch((e) => {
            console.error(e);
            window.location = window.routes.cart_url;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
        }
      }

      syncSelectedVariantId() {
        const variantInput = this.form.querySelector('[name="id"]');
        if (!variantInput) return;

        const variantPicker = this.variantPicker || this.getVariantPicker();
        if (!variantPicker) return;

        const selectedOptions = this.getSelectedOptions(variantPicker);
        const matchedVariant = this.getMatchedVariant(variantPicker, selectedOptions);

        if (matchedVariant?.id) {
          variantInput.value = matchedVariant.id;
        }
      }

      getVariantPicker() {
        const sectionId = this.dataset.sectionId;
        if (sectionId) {
          const pickerById = document.getElementById(`variant-selects-${sectionId}`);
          if (pickerById) return pickerById;
        }

        return this.closest('section')?.querySelector('variant-selects');
      }

      getSelectedOptions(variantPicker) {
        return Array.from(variantPicker.querySelectorAll('select, fieldset'), (element) => {
          if (element.tagName === 'SELECT') return element.value;
          if (element.tagName === 'FIELDSET') {
            return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
          }

          return undefined;
        }).filter((value) => value !== undefined);
      }

      getVariantData(variantPicker) {
        if (!variantPicker) return [];
        if (typeof variantPicker.getVariantData === 'function') {
          return variantPicker.getVariantData() || [];
        }

        if (!variantPicker.variantData) {
          const variantDataScript = variantPicker.querySelector('script[type="application/json"]');
          variantPicker.variantData = variantDataScript ? JSON.parse(variantDataScript.textContent) : [];
        }

        return variantPicker.variantData || [];
      }

      getMatchedVariant(variantPicker, selectedOptions) {
        if (variantPicker?.currentVariant?.id) return variantPicker.currentVariant;

        return this.getVariantData(variantPicker).find((variant) =>
          this.variantMatchesSelectedOptions(variant, selectedOptions)
        );
      }

      variantMatchesSelectedOptions(variant, selectedOptions) {
        if (!variant?.options || !selectedOptions.length) return false;

        return selectedOptions.every((selectedOption, index) => variant.options[index] === selectedOption);
      }

      updateBrowserVariantUrl() {
        const variantPicker = this.variantPicker || this.getVariantPicker();
        if (!variantPicker?.dataset?.url) return;

        const selectedOptions = this.getSelectedOptions(variantPicker);
        const matchedVariant = this.getMatchedVariant(variantPicker, selectedOptions);
        if (!matchedVariant?.id) return;

        window.history.replaceState({}, '', `${variantPicker.dataset.url}`);
      }
    }
  );
}
